import { FunnelId } from 'constants/funnel';
import clsx from 'clsx';
import ServiceFee from 'components/Pages/DoctorProfile/ServiceFee';
import ExternalFunnelButton from 'components/Shared/ExternalFunnel/ExternalFunnelButton';
import Popover from 'components/ui/Popover';
import SlideOut from 'components/ui/SlideOut';
import { useAnalytics } from 'hooks/useAnalytics';
import { useTranslation } from 'hooks/useTranslation';
import { useCallback, useEffect, useState } from 'react';
import { findViewport } from 'utils/breakpoints';
import { formatCurrency } from 'utils/currency';
import { throttle } from 'utils/throttle';
import PackagePrice from './PackagePrice';
import styles from './Packages.module.scss';
import type { IPackagesFields } from 'types/contentful/generated';
import type { ClinicPackagesCollection, TreatmentEntry } from 'types/Clinic';

type PackageKeys = 'best value' | 'most popular' | 'premium' | 'deal' | 'basic';

type PackageData = {
  key: PackageKeys;
  background: string;
  border: string;
  color: string;
  titleColor: string;
  translation: string;
};

const packageData: PackageData[] = [
  {
    key: 'best value',
    background: '!bg-secondary',
    border: '!border-secondary',
    color: '!text-black',
    titleColor: '!text-black',
    translation: 'doctorProfile.packages.bestValue',
  },
  {
    key: 'most popular',
    background: '!bg-light-blue',
    border: '!border-light-blue',
    color: '!text-white',
    titleColor: '!text-light-blue',
    translation: 'doctorProfile.packages.mostPopular',
  },
  {
    key: 'premium',
    background: '!bg-navy',
    border: '!border-navy',
    color: '!text-white',
    titleColor: '!text-navy',
    translation: 'doctorProfile.packages.Premium',
  },
  {
    key: 'deal',
    background: '!bg-dusty-orange',
    border: '!border-dusty-orange',
    color: '!text-white',
    titleColor: '!text-dusty-orange',
    translation: 'doctorProfile.packages.deal',
  },
  {
    key: 'basic',
    background: '!bg-grey-gold',
    border: '!border-grey-gold',
    color: '!text-white',
    titleColor: '!text-grey-gold',
    translation: 'doctorProfile.packages.basic',
  },
];

export type PackagesCollection = {
  items: PackageEntry[];
};

export type PackageEntry = IPackagesFields;

const packageCols: Record<number, string | undefined> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-3',
};

const getPackageColor = (value: string): PackageData => {
  return (
    packageData.find((item) => item.key === value) ||
    (packageData.find((item) => item.key === 'best value') as PackageData)
  );
};

type Props = {
  packages: ClinicPackagesCollection;
  treatment: TreatmentEntry;
};

const Packages = ({ packages }: Props): JSX.Element | null => {
  const filteredPackages = packages.items.filter(
    // the null check is important for specific doctor pages
    (p) => p && !p.doNotShowOnProfile,
  );

  const packagesCount = filteredPackages.length;

  const hasManyPackages = packages && packagesCount > 3;

  const { locale, t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const [isServiceFeeOpen, setIsServiceFeeOpen] = useState(false);
  const [packagePrice, setPackagePrice] = useState<number | null>(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [popoverTarget, setPopoverTarget] = useState<HTMLElement | null>(null);

  const checkViewport = useCallback((newWidth: number) => {
    const viewportName = findViewport(newWidth);
    if (
      'md' === viewportName ||
      'xl' === viewportName ||
      'lg' === viewportName
    ) {
      setIsSmallScreen(false);
    }

    if ('sm' === viewportName || 'xs' === viewportName || '' === viewportName) {
      setIsSmallScreen(true);
    }
  }, []);

  useEffect(() => {
    checkViewport(window.innerWidth);

    const throttledResizeListener = throttle(
      () => checkViewport(window.innerWidth),
      200,
    );

    window.addEventListener('resize', throttledResizeListener, {
      passive: true,
    });

    return () => {
      window.removeEventListener('resize', throttledResizeListener);
    };
  }, []);

  if (!packagesCount || !filteredPackages[0].name) {
    return null;
  }

  const openServiceFee = (price: number, element: HTMLElement | null): void => {
    if (!isServiceFeeOpen) {
      setIsServiceFeeOpen(true);
      setPackagePrice(price);
      setPopoverTarget(element);
    }
  };

  const onServiceFeeClose = (): void => {
    setIsServiceFeeOpen(false);
    setPackagePrice(null);
    setPopoverTarget(null);
  };

  const getGridTemplateColumns = (): string => {
    let times = packagesCount;
    if (!isSmallScreen) {
      times = Math.min(packagesCount, 3);
    }

    let trackList = 'minmax(0, 1fr)';
    if (isSmallScreen && packagesCount > 2) {
      trackList = '80%';
    }

    return `repeat(${times}, ${trackList})`;
  };

  return (
    <div>
      <div
        className={clsx(
          'max-w-screen-xl mx-auto px-5 pb-6 md:py-12 md:px-4 lg:grid',
          hasManyPackages ? 'md:grid-cols-6' : 'md:grid-cols-3',
        )}
      >
        <div>
          <h2 className="adelle-bold-section-headline">{t('prices')}</h2>
        </div>
        <div
          className={clsx(
            hasManyPackages ? 'col-span-5' : 'col-span-2',
            'overflow-x-auto md:overflow-visible',
          )}
        >
          <div
            className={clsx(
              'grid',
              packagesCount <= 4
                ? `${packageCols[packagesCount]}`
                : 'md:grid-cols-3',
            )}
            style={{
              gridTemplateColumns: getGridTemplateColumns(),
            }}
          >
            {!isSmallScreen && isServiceFeeOpen && !!packagePrice && (
              <Popover
                className="p-6 md:w-112"
                placement="bottom"
                open={isServiceFeeOpen}
                onClose={onServiceFeeClose}
                target={popoverTarget}
              >
                <ServiceFee packagePrice={packagePrice} />
              </Popover>
            )}
            {filteredPackages.map((currentPackage, index) => {
              const { color, background, border, titleColor, translation } =
                getPackageColor(
                  currentPackage.deal ? 'deal' : currentPackage.type,
                );

              return (
                <div
                  key={`currentPackage.fields.id_${index}`}
                  className="flex mx-2 md:transition md:duration-500 md:ease-in-out md:transform md:hover:scale-105 mb-4"
                >
                  <div
                    className={clsx(
                      'flex flex-col w-full justify-between bg-white pt-6 rounded-lg overflow-hidden border',
                      border,
                    )}
                  >
                    <div className="px-2">
                      <p
                        className={clsx(
                          'text-center text-xs font-bold uppercase mb-2',
                          titleColor,
                        )}
                      >
                        {t(translation)}
                      </p>
                      <h3 className={styles.packageName}>
                        {currentPackage.name}
                      </h3>
                      <p className="text-center text-primary mb-6">
                        {currentPackage.description}
                      </p>
                    </div>
                    <div>
                      <div className="px-3">
                        <div className="border-t grid grid-cols-2 py-2">
                          {!!currentPackage.fullDays && (
                            <div className="text-center border-r flex items-center justify-center">
                              <span>
                                <span className="font-bold text-2xl">
                                  {currentPackage.fullDays}
                                </span>{' '}
                                {t('doctorProfile.packages.nights')}
                              </span>
                            </div>
                          )}
                          <div
                            className={clsx(
                              'text-center',
                              !currentPackage.fullDays && 'col-span-2',
                            )}
                          >
                            {currentPackage.deal &&
                              currentPackage.discountedPriceEuros && (
                                <span className="font-bold line-through block">
                                  {formatCurrency(
                                    currentPackage.price,
                                    'EUR',
                                    locale,
                                  )}
                                </span>
                              )}
                            <PackagePrice
                              price={currentPackage.price}
                              deal={currentPackage.deal}
                              discountedPriceEuros={
                                currentPackage.discountedPriceEuros
                              }
                              locale={locale}
                              triggerPopover={(price, element) =>
                                openServiceFee(price, element)
                              }
                              isStartingFromPrice={
                                currentPackage.startingFromPrice
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <ExternalFunnelButton
                        funnelId={FunnelId.CONTACT_FORM}
                        renderButton={(openFunnel) => {
                          return (
                            <button
                              className={clsx(
                                'w-full text-center py-3',
                                color,
                                background,
                              )}
                              onClick={() => {
                                trackEvent({
                                  category: 'CTA',
                                  action: 'CTA|Click',
                                  label: `CTA|DoctorProfile|Prices|Book|${currentPackage.name}`,
                                });

                                onServiceFeeClose();

                                openFunnel();
                              }}
                            >
                              {t('toBook')}
                            </button>
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isSmallScreen && !!packagePrice && (
        <SlideOut
          onClose={onServiceFeeClose}
          open={isServiceFeeOpen}
          placement="bottom"
          className="rounded-t-lg"
        >
          <ServiceFee className="px-5 pt-8 pb-4" packagePrice={packagePrice} />
        </SlideOut>
      )}
    </div>
  );
};

export default Packages;
